import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * 字體與語言切換下拉選單組件
 * 整合字體選擇（圓體/明體）和語言選擇（未來可擴展英文）
 */
const FontLanguageDropdown = ({
  currentFont,
  currentLanguage,
  onFontChange,
  onLanguageChange,
  showLanguageOptions = false, // 控制是否顯示語言選項
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // 點擊外部關閉下拉選單
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 字體選項
  const fontOptions = [
    { value: 'primary', label: '圓體', icon: 'text_fields' },
    { value: 'secondary', label: '明體', icon: 'title' },
  ];

  // 語言選項（可控制是否顯示）
  const languageOptions = showLanguageOptions ? [
    { value: 'zh-TW', label: '繁體中文', shortLabel: '中', flag: '🇹🇼' },
    { value: 'en', label: 'English', shortLabel: 'EN', flag: '🇺🇸' },
  ] : [];

  const handleFontSelect = (fontValue) => {
    onFontChange(fontValue);
    setIsOpen(false);
  };

  const handleLanguageSelect = (languageCode) => {
    onLanguageChange(languageCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 觸發按鈕 */}
      <button
        className="w-9 h-9 flex items-center justify-center text-stone-500 bg-stone-50 rounded-full border border-stone-100 hover:bg-stone-100 transition-colors cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        title="字體與語言設定"
      >
        <span className="material-symbols-outlined text-xl">text_fields</span>
      </button>

      {/* 下拉選單 */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-40 bg-white rounded-lg shadow-lg border border-stone-200 py-2 z-50">
          {/* 字體選項區塊 */}
          <div className="px-3 py-1">
            <div className="text-xs text-stone-400 font-medium mb-1">字體</div>
            {fontOptions.map((font) => (
              <button
                key={font.value}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  currentFont === font.value
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-stone-700 hover:bg-stone-50'
                }`}
                onClick={() => handleFontSelect(font.value)}
              >
                <span className="material-symbols-outlined text-lg">{font.icon}</span>
                <span>{font.label}</span>
                {currentFont === font.value && (
                  <span className="material-symbols-outlined text-sm ml-auto">check</span>
                )}
              </button>
            ))}
          </div>

          {/* 語言選項區塊（條件顯示）*/}
          {showLanguageOptions && languageOptions.length > 0 && (
            <>
              <div className="border-t border-stone-100 my-2"></div>
              <div className="px-3 py-1">
                <div className="text-xs text-stone-400 font-medium mb-1">語言</div>
                {languageOptions.map((lang) => (
                  <button
                    key={lang.value}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                      currentLanguage === lang.value
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-stone-700 hover:bg-stone-50'
                    }`}
                    onClick={() => handleLanguageSelect(lang.value)}
                  >
                    <span className="text-base">{lang.flag}</span>
                    <span>{lang.label}</span>
                    {currentLanguage === lang.value && (
                      <span className="material-symbols-outlined text-sm ml-auto">check</span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

FontLanguageDropdown.propTypes = {
  currentFont: PropTypes.string.isRequired,
  currentLanguage: PropTypes.string.isRequired,
  onFontChange: PropTypes.func.isRequired,
  onLanguageChange: PropTypes.func.isRequired,
  showLanguageOptions: PropTypes.bool,
};

export default FontLanguageDropdown;
